/**
 * MiniAppSandbox
 *
 * Renders each mini-app in a separate React root mounted directly into the
 * host document (no Shadow DOM). CSS from the mini-app bundle is injected as a
 * <style> tag in <head>, keyed by appId so it is replaced on re-load and
 * removed when the sandbox unmounts.
 *
 * Architecture:
 *  ┌── host React root ──────────────────────────────────────────┐
 *  │  <MiniAppSandbox>                                           │
 *  │    <div ref data-theme="light|dark">  ← mount host         │
 *  │      <div>  ← mini-app React root mount point              │
 *  │        <MemoryRouter>                                       │
 *  │          <SDKProvider theme=… navigate=…>                   │
 *  │            <AppRouter />  ← mini-app code                   │
 *  └─────────────────────────────────────────────────────────────┘
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { MemoryRouter, useLocation } from 'react-router-dom';
import { SDKProvider } from '@next-qr/mini-app-sdk';
import type { AppNavigate, Theme } from '@next-qr/mini-app-sdk';
import { useTranslation } from 'react-i18next';
import React from 'react';

import { loadMiniApp, clearCache } from '../miniApps/bundleLoader';

// ── RouterSyncer ──────────────────────────────────────────────────────────────
// Rendered inside the mini-app's MemoryRouter. Watches location changes and
// calls onNavigate so the host's BrowserRouter stays in sync.
function RouterSyncer({ onNavigate }: { onNavigate: (subPath: string) => void }) {
  const location = useLocation();
  const firstRender = useRef(true);

  useEffect(() => {
    // Skip the initial render — the MemoryRouter just initialised at initialPath.
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    onNavigate(location.pathname);
  }, [location.pathname, onNavigate]);

  return null;
}

interface MiniAppSandboxProps {
  appId: string;
  theme: Theme;
  /** Host-level navigator (toHost / toMiniApp / goBack). */
  navigate: AppNavigate;
  /** Initial sub-path inside the mini-app (e.g. '/conversation/3'). Defaults to '/'. */
  initialPath?: string;
  /** Called whenever the mini-app navigates internally, so the host URL stays in sync. */
  onNavigate?: (subPath: string) => void;
}

export function MiniAppSandbox({ appId, theme, navigate: hostNavigate, initialPath = '/', onNavigate }: MiniAppSandboxProps) {
  const { t } = useTranslation();
  const hostRef = useRef<HTMLDivElement>(null);
  const reactRootRef = useRef<ReturnType<typeof createRoot> | null>(null);
  const AppRouterRef = useRef<React.ComponentType | null>(null);
  const cssStyleRef = useRef<HTMLStyleElement | null>(null);

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // ── Render (or re-render) the mini-app into the React root ───────────────
  const renderIntoContainer = useCallback(() => {
    const reactRoot = reactRootRef.current;
    const AppRouter = AppRouterRef.current;
    if (!reactRoot || !AppRouter) return;

    reactRoot.render(
      <MemoryRouter initialEntries={[initialPath]} initialIndex={0}>
        {onNavigate && <RouterSyncer onNavigate={onNavigate} />}
        <SDKProvider appId={appId} platform="web" navigate={hostNavigate} theme={theme}>
          <AppRouter />
        </SDKProvider>
      </MemoryRouter>,
    );
  }, [appId, theme, hostNavigate, initialPath, onNavigate]);

  // ── Load (or re-load) the mini-app bundle ─────────────────────────────────
  const load = useCallback(async () => {
    setStatus('loading');
    setErrorMsg(null);
    try {
      const result = await loadMiniApp(appId);
      if (result) {
        // Replace the mini-app CSS <style> tag in <head>.
        cssStyleRef.current?.remove();
        cssStyleRef.current = null;
        if (result.css) {
          const style = document.createElement('style');
          style.setAttribute('data-mini-app', appId);
          style.textContent = result.css;
          document.head.appendChild(style);
          cssStyleRef.current = style;
        }
        AppRouterRef.current = result.Router;
        setStatus('ready');
      } else {
        setStatus('error');
        setErrorMsg(t('miniApp.loadFailed'));
      }
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err.message || t('miniApp.loadFailed'));
    }
  }, [appId, t]);

  // ── Initialize the React root once (after first mount) ───────────────────
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const mount = document.createElement('div');
    mount.style.cssText = 'height:100%;display:contents';
    host.appendChild(mount);
    reactRootRef.current = createRoot(mount);

    return () => {
      const root = reactRootRef.current;
      reactRootRef.current = null;
      // Defer unmount to avoid "unmount during render" warnings.
      setTimeout(() => {
        root?.unmount();
        mount.remove();
      }, 0);
    };
  }, []); // intentionally empty — runs once after mount

  // ── Cleanup CSS when sandbox unmounts ─────────────────────────────────────
  useEffect(() => {
    return () => {
      cssStyleRef.current?.remove();
      cssStyleRef.current = null;
    };
  }, []);

  // ── Load when appId changes ───────────────────────────────────────────────
  useEffect(() => {
    load();
  }, [load]); // `load` changes when `appId` changes

  // ── Re-render when props or load status changes ───────────────────────────
  useEffect(() => {
    if (status === 'ready') renderIntoContainer();
  }, [status, renderIntoContainer]);

  // ── Dev hot-reload ────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.appId === appId) {
        clearCache(appId);
        AppRouterRef.current = null;
        load();
      }
    };
    window.addEventListener('mini-app-dev-reload', handler);
    return () => window.removeEventListener('mini-app-dev-reload', handler);
  }, [appId, load]);

  // ── OTA update ────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.appId === appId) {
        AppRouterRef.current = null;
        load();
      }
    };
    window.addEventListener('mini-app-ota-update', handler);
    return () => window.removeEventListener('mini-app-ota-update', handler);
  }, [appId, load]);

  const themeAttr = theme.dark ? 'dark' : 'light';

  return (
    <div className="relative h-full">
      {/* Loading overlay */}
      {status === 'loading' && (
        <div className="absolute inset-0 z-10 flex items-center justify-center text-gray-400">
          {t('miniApp.loading')}
        </div>
      )}

      {/* Error overlay */}
      {status === 'error' && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4">
          <span className="text-5xl">⚠️</span>
          <p className="text-gray-600">{errorMsg || t('miniApp.notFound')}</p>
          <button
            onClick={() => hostNavigate.toHost('Home')}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            {t('miniApp.goHome')}
          </button>
        </div>
      )}

      {/* Mount point — mini-app React root lives here */}
      <div ref={hostRef} data-theme={themeAttr} className="h-full" />
    </div>
  );
}
